import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { UpsellProduct } from '@getjust/gateway';

import { useCart, useShop } from '$hooks/queries';
import { useCheckoutFeaturesAtom } from '$src/hooks/state/useCheckoutFeaturesAtom';
import { useInitialDistinctIdRO } from '$src/hooks/state/useInitialDistinctId';
import { gateway } from '$src/http';
import { posthog } from '$src/utils';

export const UPSELL_QUERY_KEY = 'QUERY/UPSELL_QUERY_KEY';

type UpsellProductsData = {
  upsellProducts: UpsellProduct[];
  smartUpsellAbTestVariant: string | undefined;
};

export function useUpsellProducts() {
  const { data: shop } = useShop();
  const { data: cart } = useCart();
  const { canUseCheckoutFeature } = useCheckoutFeaturesAtom();
  const initialDistinctId = useInitialDistinctIdRO();

  const isUpsellEnabled = useMemo((): boolean => {
    return Boolean(shop?.upsell?.active && canUseCheckoutFeature('upsell'));
  }, [shop?.upsell?.active, canUseCheckoutFeature]);

  const productsInCart = useMemo(
    () =>
      cart?.lineItems?.map((item) => ({
        productId: item.productId,
        variantId: item.variantId,
        source: 'IN_CART',
      })) ?? [],
    [cart?.lineItems],
  );

  return useQuery({
    queryKey: [UPSELL_QUERY_KEY, cart?.id],
    enabled: Boolean(shop?.id && cart?.id && productsInCart?.length && isUpsellEnabled),
    queryFn: async (): Promise<UpsellProductsData> => {
      const abTestSmartUpsellFlag = await posthog.getFeatureFlag('smart-upsell');
      const smartUpsellAbTestVariant = String(abTestSmartUpsellFlag);
      const isSmartUpsellEnabled =
        canUseCheckoutFeature('smart_upsell') && smartUpsellAbTestVariant === 'with-smart-upsell';

      const response = await gateway.post<UpsellProduct[]>(`/api/${shop?.id}/upsell-products`, {
        productsInCart,
        intent: (shop?.upsell?.intent || 'related').toUpperCase(),
        isSmartUpsellEnabled,
        userDistinctId: initialDistinctId,
      });

      const upsellProducts: UpsellProduct[] = response.data ?? [];
      const isOnlySmartProducts = upsellProducts.every((p) => p.source === 'smart');

      return {
        // Temporary product decision: Only smart products -> don't display upsell
        upsellProducts: isOnlySmartProducts ? [] : upsellProducts,
        smartUpsellAbTestVariant,
      };
    },
  });
}
